/**
 * The document, edited as it reads (user call, 12 Oct: no raw markdown, no
 * block-by-block boxes). The whole document is one rich text surface; the
 * format bar floats over the passage the caret is in (docked at the foot on a
 * phone) and works on the selection: Heading 2 and 3 (again turns it back into
 * text), bold, italic, a list, a link, and Rewrite, which quotes the passage in
 * the chat. Every change is written back as markdown (richMarkdown.ts); saving
 * stores that text as the next version.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { FormatAction } from "./docFormat";
import { DocFormatBar } from "./ArtifactStrips";
import { htmlToMarkdown, markdownToHtml } from "./richMarkdown";

type Props = {
  /** The text the editor opens with. Re-keyed by the caller to start over. */
  markdown: string;
  /** Every change, as markdown. The first call is the opened text as written back. */
  onChange: (markdown: string, opened: boolean) => void;
  /** Quote the passage the caret is in, in the chat. */
  onRewrite?: (text: string) => void;
  isPhone: boolean;
  label: string;
};

/** The editor's direct child holding the caret: the passage the bar works on. */
function passageOf(root: HTMLElement): HTMLElement | null {
  const selection = window.getSelection();
  const node = selection?.anchorNode ?? null;
  if (!node || !root.contains(node)) return null;
  let el: Node | null = node;
  while (el && el.parentNode !== root) el = el.parentNode;
  return el instanceof HTMLElement ? el : null;
}

function inside(root: HTMLElement, tag: string): HTMLElement | null {
  const node = window.getSelection()?.anchorNode ?? null;
  let el: Node | null = node;
  while (el && el !== root) {
    if (el instanceof HTMLElement && el.tagName === tag) return el;
    el = el.parentNode;
  }
  return null;
}

export default function RichDocEditor({ markdown, onChange, onRewrite, isPhone, label }: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });
  const [focused, setFocused] = useState(false);
  const [barTop, setBarTop] = useState<number | null>(null);
  const frame = useRef(0);

  // The opened text is rendered once; from then on the DOM is the draft.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    root.innerHTML = markdownToHtml(markdown);
    onChangeRef.current(htmlToMarkdown(root), true);
    // Only the first render: the caller re-keys the editor to load other text.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => {
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      const root = rootRef.current;
      if (root) onChangeRef.current(htmlToMarkdown(root), false);
    });
  };
  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  // The bar follows the passage with the caret.
  useEffect(() => {
    if (!focused || isPhone) return;
    const place = () => {
      const root = rootRef.current;
      const passage = root ? passageOf(root) : null;
      setBarTop(passage ? passage.offsetTop : null);
    };
    place();
    document.addEventListener("selectionchange", place);
    return () => document.removeEventListener("selectionchange", place);
  }, [focused, isPhone]);

  const format = (action: FormatAction) => {
    const root = rootRef.current;
    if (!root) return;
    root.focus({ preventScroll: true });
    if (action === "bold") document.execCommand("bold");
    else if (action === "italic") document.execCommand("italic");
    else if (action === "list") document.execCommand("insertUnorderedList");
    else if (action === "h2" || action === "h3") {
      const tag = action.toUpperCase();
      const passage = passageOf(root);
      document.execCommand("formatBlock", false, passage?.tagName === tag ? "p" : tag.toLowerCase());
    } else if (action === "link") {
      const link = inside(root, "A");
      if (link) {
        document.execCommand("unlink");
      } else {
        const href = window.prompt("Link to", "https://")?.trim();
        if (href && /^(?:https?:|mailto:|\/|#)/i.test(href)) {
          if (window.getSelection()?.isCollapsed) {
            // Nothing selected: the address goes in as its own link.
            const safe = href.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
            document.execCommand("insertHTML", false, `<a href="${safe}">${safe}</a>`);
          } else {
            document.execCommand("createLink", false, href);
          }
        }
      }
    }
    emit();
  };

  const rewrite = onRewrite
    ? () => {
        const root = rootRef.current;
        const selected = window.getSelection()?.toString().trim();
        const passage = root ? passageOf(root) : null;
        const text = selected || passage?.innerText.trim();
        if (text) onRewrite(text);
      }
    : undefined;

  return (
    <div className="ws-rich-wrap" data-phone={isPhone || undefined}>
      {!isPhone && focused && barTop !== null ? (
        <div className="ws-rich-bar" style={{ top: barTop }}>
          <DocFormatBar docked={false} onFormat={format} onRewrite={rewrite} />
        </div>
      ) : null}
      <div
        ref={rootRef}
        className="ws-rich"
        role="textbox"
        aria-multiline="true"
        aria-label={label}
        contentEditable
        suppressContentEditableWarning
        spellCheck
        onInput={emit}
        onFocus={() => {
          // Enter starts a paragraph, not a div.
          document.execCommand("defaultParagraphSeparator", false, "p");
          setFocused(true);
        }}
        onBlur={(event) => {
          // The bar keeps the selection (its buttons never take focus from it).
          if (event.relatedTarget instanceof Node && event.currentTarget.parentElement?.contains(event.relatedTarget)) return;
          setFocused(false);
        }}
        onPaste={(event) => {
          // Pasted text comes in as words, never as another page's markup.
          event.preventDefault();
          const text = event.clipboardData.getData("text/plain");
          if (text) document.execCommand("insertText", false, text);
        }}
        onKeyDown={(event) => {
          const mod = event.metaKey || event.ctrlKey;
          if (!mod) return;
          const key = event.key.toLowerCase();
          if (key === "b" || key === "i") {
            event.preventDefault();
            format(key === "b" ? "bold" : "italic");
          } else if (key === "k") {
            event.preventDefault();
            format("link");
          }
        }}
      />
      {isPhone && focused ? <DocFormatBar docked onFormat={format} onRewrite={rewrite} /> : null}
    </div>
  );
}
